import * as i0 from '@angular/core';
import { EventEmitter, HostListener, HostBinding, Output, Input, Directive, ViewChild, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent, CentricButtonModule } from '@windmill/ng-windmill/button';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';

const backgroundClass = 'centric-upload-file-container';
/**
 * import { CentricUploadAreaModule } from '@windmill/ng-windmill/upload-area';
 */
class DragDropDirective {
    /**
     * Whether the component is disabled
     */
    disabled = false;
    /**
     * Choose the type of upload file
     */
    type = 'drop-area';
    /**
     * Emits files list on drop event
     */
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    onFileDropped = new EventEmitter();
    background = backgroundClass;
    onDragOver(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.disabled && this.type !== 'button') {
            this.getUploadClasses('drag-over');
        }
    }
    onDragLeave(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.disabled && this.type !== 'button') {
            this.removeUploadClasses();
        }
    }
    ondrop(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.disabled && this.type !== 'button') {
            const files = event.dataTransfer?.files;
            if (files && files.length > 0) {
                this.onFileDropped.emit(files);
            }
        }
        this.removeUploadClasses();
    }
    getUploadClasses(event) {
        this.background = `${backgroundClass} ${event}`;
    }
    removeUploadClasses() {
        this.background = backgroundClass;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DragDropDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: DragDropDirective, isStandalone: true, selector: "[appDragDrop]", inputs: { disabled: "disabled", type: "type" }, outputs: { onFileDropped: "onFileDropped" }, host: { listeners: { "dragover": "onDragOver($event)", "dragleave": "onDragLeave($event)", "drop": "ondrop($event)" }, properties: { "class": "this.background" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DragDropDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[appDragDrop]',
                    standalone: true
                }]
        }], propDecorators: { disabled: [{
                type: Input
            }], type: [{
                type: Input
            }], onFileDropped: [{
                type: Output
            }], background: [{
                type: HostBinding,
                args: ['class']
            }], onDragOver: [{
                type: HostListener,
                args: ['dragover', ['$event']]
            }], onDragLeave: [{
                type: HostListener,
                args: ['dragleave', ['$event']]
            }], ondrop: [{
                type: HostListener,
                args: ['drop', ['$event']]
            }] } });

const windmillComponents = [ButtonComponent, CentricIcon2Component];
/**
 * To use this component in a module, you can import the `CentricUploadAreaModule`:
 * @example
 * ```typescript
 * import { CentricUploadAreaModule } from '@windmill/ng-windmill/upload-area';
 * ```
 *
 * To use the standalone component directly, you can import `UploadAreaComponent`:
 * @example
 * ```typescript
 * import { UploadAreaComponent } from '@windmill/ng-windmill/upload-area';
 * ```
 */
class UploadAreaComponent {
    /**
     * The accept attribute takes a string containing one or more of unique file type specifiers as its value
     */
    accept;
    /**
     * Used to store and manipulate files
     */
    files = [];
    /**
     * Used for displaying the message/instructions
     */
    message;
    /**
     * Used for displaying additional instructions
     */
    extraMessage;
    /**
     * Used for displaying the button text
     */
    buttonText;
    /**
     * Used to choose what type of upload file do you want to use
     */
    type = 'drop-area';
    /**
     * Used for uploading multiple documents at time
     */
    multiple = false;
    /**
     * Whether the component is disabled
     */
    disabled = false;
    /**
     * The id for upload area input
     */
    id;
    /**
     * The data-testid for upload button
     */
    testButtonUploadId;
    /**
     * Sets the aria-label attribute for the upload button
     */
    ariaLabelButtonUpload;
    /**
     * Sets the aria-labelledby attribute for the upload button
     */
    ariaLabelledbyButtonUpload;
    /**
     * Sets the aria-description attribute for the upload button
     */
    ariaDescriptionButtonUpload;
    /**
     * Sets the aria-describedby attribute for the upload button
     */
    ariaDescribedButtonUpload;
    /**
     * Emits a File array containing all files that have been added through upload actions
     */
    filesUploaded = new EventEmitter();
    /**
     * Emits a File object when upload a duplicated file
     */
    fileDuplicated = new EventEmitter();
    /**
     * Emits a File array containing only files that have been added in the last upload action
     */
    filesLastAdded = new EventEmitter();
    uploadElRef;
    currentlyAddedFiles = [];
    get displayMessage() {
        return Boolean(this.message) && this.isDropAreaType;
    }
    get displayExtraMessage() {
        return Boolean(this.extraMessage) && this.isDropAreaType;
    }
    get displayIconId() {
        return this.isButtonType ? 'cloud-upload_r' : '';
    }
    get isDropAreaType() {
        return this.type === 'drop-area';
    }
    get isButtonType() {
        return this.type === 'button';
    }
    onChange(event) {
        const fileListFromEvent = event.target.files;
        if (fileListFromEvent) {
            this.uploadFile(fileListFromEvent);
        }
    }
    uploadFile(event) {
        this.currentlyAddedFiles = [];
        if (!this.multiple) {
            this.files = [];
        }
        for (const file of Array.from(event)) {
            this.processFiles(file);
        }
        this.emitFiles();
        this.resetUploadInput();
    }
    processFiles(element) {
        const duplicateFound = this.files?.find(file => file.name === element.name && file.size === element.size);
        if (duplicateFound) {
            this.fileDuplicated.emit(element);
        }
        else {
            this.currentlyAddedFiles.push(element);
            this.files?.push(element);
        }
    }
    emitFiles() {
        this.filesLastAdded.emit(this.currentlyAddedFiles);
        this.filesUploaded.emit(this.files);
    }
    resetUploadInput() {
        const inputNativeElement = this.uploadElRef.nativeElement;
        inputNativeElement.value = '';
    }
    handleChange(event) {
        if (!this.disabled) {
            this.onChange(event);
        }
    }
    handleClick() {
        if (!this.disabled) {
            this.uploadElRef.nativeElement.click();
        }
    }
    onFileDropped(event) {
        if (!this.disabled && this.isDropAreaType) {
            this.uploadFile(event);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: UploadAreaComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: UploadAreaComponent, isStandalone: true, selector: "centric-upload-area", inputs: { accept: "accept", files: "files", message: "message", extraMessage: "extraMessage", buttonText: "buttonText", type: "type", multiple: "multiple", disabled: "disabled", id: "id", testButtonUploadId: "testButtonUploadId", ariaLabelButtonUpload: "ariaLabelButtonUpload", ariaLabelledbyButtonUpload: "ariaLabelledbyButtonUpload", ariaDescriptionButtonUpload: "ariaDescriptionButtonUpload", ariaDescribedButtonUpload: "ariaDescribedButtonUpload" }, outputs: { filesUploaded: "filesUploaded", fileDuplicated: "fileDuplicated", filesLastAdded: "filesLastAdded" }, viewQueries: [{ propertyName: "uploadElRef", first: true, predicate: ["fileInput"], descendants: true }], ngImport: i0, template: "<div\r\n  appDragDrop\r\n  class=\"centric-upload-file-container\"\r\n  [class.drop-area-type]=\"isDropAreaType\"\r\n  [class.button-type]=\"isButtonType\"\r\n  [class.disabled]=\"disabled\"\r\n  [disabled]=\"disabled\"\r\n  [type]=\"type\"\r\n  (click)=\"handleClick()\"\r\n  (onFileDropped)=\"onFileDropped($event)\">\r\n  @if (isDropAreaType) {\r\n    <div class=\"upload-area-icon-container\">\r\n      <centric-icon2\r\n        class=\"upload-area-icon\"\r\n        [svgIcon]=\"'cloud-upload_r'\"\r\n        [size]=\"'40'\"></centric-icon2>\r\n    </div>\r\n  }\r\n  <input\r\n    hidden\r\n    #fileInput\r\n    [type]=\"'file'\"\r\n    [accept]=\"accept\"\r\n    [attr.id]=\"id\"\r\n    [multiple]=\"multiple\"\r\n    [disabled]=\"disabled\"\r\n    [attr.aria-label]=\"buttonText\"\r\n    (change)=\"handleChange($event)\" />\r\n  <div class=\"message-container\">\r\n    @if (displayMessage) {\r\n      <p class=\"message-text\">{{ message }}</p>\r\n    }\r\n    @if (displayExtraMessage) {\r\n      <p>{{ extraMessage }}</p>\r\n    }\r\n    <centric-button\r\n      [size]=\"'small'\"\r\n      [buttonType]=\"'button'\"\r\n      [iconId]=\"displayIconId\"\r\n      [disabled]=\"disabled\"\r\n      [testButtonId]=\"testButtonUploadId\"\r\n      [ariaLabel]=\"ariaLabelButtonUpload\"\r\n      [ariaLabelledby]=\"ariaLabelledbyButtonUpload\"\r\n      [ariaDescription]=\"ariaDescriptionButtonUpload\"\r\n      [ariaDescribedby]=\"ariaDescribedButtonUpload\"\r\n      (click)=\"onChange($event)\">\r\n      {{ buttonText }}\r\n    </centric-button>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: DragDropDirective, selector: "[appDragDrop]", inputs: ["disabled", "type"], outputs: ["onFileDropped"] }, { kind: "component", type: ButtonComponent, selector: "centric-button", inputs: ["buttonType", "testButtonId", "disabled", "ariaHasPopup", "ariaControls", "ariaExpanded"] }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: UploadAreaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-upload-area', encapsulation: ViewEncapsulation.None, imports: [CommonModule, DragDropDirective, ...windmillComponents], standalone: true, template: "<div\r\n  appDragDrop\r\n  class=\"centric-upload-file-container\"\r\n  [class.drop-area-type]=\"isDropAreaType\"\r\n  [class.button-type]=\"isButtonType\"\r\n  [class.disabled]=\"disabled\"\r\n  [disabled]=\"disabled\"\r\n  [type]=\"type\"\r\n  (click)=\"handleClick()\"\r\n  (onFileDropped)=\"onFileDropped($event)\">\r\n  @if (isDropAreaType) {\r\n    <div class=\"upload-area-icon-container\">\r\n      <centric-icon2\r\n        class=\"upload-area-icon\"\r\n        [svgIcon]=\"'cloud-upload_r'\"\r\n        [size]=\"'40'\"></centric-icon2>\r\n    </div>\r\n  }\r\n  <input\r\n    hidden\r\n    #fileInput\r\n    [type]=\"'file'\"\r\n    [accept]=\"accept\"\r\n    [attr.id]=\"id\"\r\n    [multiple]=\"multiple\"\r\n    [disabled]=\"disabled\"\r\n    [attr.aria-label]=\"buttonText\"\r\n    (change)=\"handleChange($event)\" />\r\n  <div class=\"message-container\">\r\n    @if (displayMessage) {\r\n      <p class=\"message-text\">{{ message }}</p>\r\n    }\r\n    @if (displayExtraMessage) {\r\n      <p>{{ extraMessage }}</p>\r\n    }\r\n    <centric-button\r\n      [size]=\"'small'\"\r\n      [buttonType]=\"'button'\"\r\n      [iconId]=\"displayIconId\"\r\n      [disabled]=\"disabled\"\r\n      [testButtonId]=\"testButtonUploadId\"\r\n      [ariaLabel]=\"ariaLabelButtonUpload\"\r\n      [ariaLabelledby]=\"ariaLabelledbyButtonUpload\"\r\n      [ariaDescription]=\"ariaDescriptionButtonUpload\"\r\n      [ariaDescribedby]=\"ariaDescribedButtonUpload\"\r\n      (click)=\"onChange($event)\">\r\n      {{ buttonText }}\r\n    </centric-button>\r\n  </div>\r\n</div>\r\n" }]
        }], propDecorators: { accept: [{
                type: Input
            }], files: [{
                type: Input
            }], message: [{
                type: Input
            }], extraMessage: [{
                type: Input
            }], buttonText: [{
                type: Input
            }], type: [{
                type: Input
            }], multiple: [{
                type: Input
            }], disabled: [{
                type: Input
            }], id: [{
                type: Input
            }], testButtonUploadId: [{
                type: Input
            }], ariaLabelButtonUpload: [{
                type: Input
            }], ariaLabelledbyButtonUpload: [{
                type: Input
            }], ariaDescriptionButtonUpload: [{
                type: Input
            }], ariaDescribedButtonUpload: [{
                type: Input
            }], filesUploaded: [{
                type: Output
            }], fileDuplicated: [{
                type: Output
            }], filesLastAdded: [{
                type: Output
            }], uploadElRef: [{
                type: ViewChild,
                args: ['fileInput']
            }] } });

const windmillModules = [CentricButtonModule, CentricIcon2Module];
const moduleComponents = [DragDropDirective, UploadAreaComponent];
class CentricUploadAreaModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricUploadAreaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricUploadAreaModule, imports: [CommonModule, CentricButtonModule, CentricIcon2Module, DragDropDirective, UploadAreaComponent], exports: [DragDropDirective, UploadAreaComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricUploadAreaModule, imports: [CommonModule, windmillModules, UploadAreaComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricUploadAreaModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ...windmillModules, ...moduleComponents],
                    exports: [...moduleComponents]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CentricUploadAreaModule, DragDropDirective, UploadAreaComponent };
